import directMessagesService from '../services/directMessages';
import posthog from '../utils/posthog';
import type { Conversation, ConversationMessage, DirectMessagesState, ReduxAction } from '../types/common';
import type { AppDispatch } from '../store';

const initialState: DirectMessagesState = {
  conversations: [],
  activeConversationId: null,
  messages: {},
  conversationLoading: {},
  conversationError: {},
  listLoading: false,
  listError: null,
  optimisticMessages: {},
  paginationCursors: {},
};

const directMessagesReducer = (
  state: DirectMessagesState = initialState,
  action: ReduxAction<unknown>
): DirectMessagesState => {
  switch (action.type) {
    case 'SET_DM_LIST_LOADING':
      return {
        ...state,
        listLoading: true,
        listError: null,
      };

    case 'SET_DM_CONVERSATIONS':
      return {
        ...state,
        // The currently-open conversation is being viewed, so it has nothing
        // unread to show. Forcing it to 0 here avoids a flicker when a refetch
        // races the mark-as-read request for a message that just arrived.
        conversations: (action.payload as Conversation[]).map((conv) =>
          conv._id === state.activeConversationId
            ? { ...conv, unreadCount: 0 }
            : conv
        ),
        listLoading: false,
      };

    case 'SET_DM_LIST_ERROR':
      return {
        ...state,
        listError: action.payload as string,
        listLoading: false,
      };

    case 'SET_ACTIVE_DM_CONVERSATION':
      return {
        ...state,
        activeConversationId: action.payload as string,
        conversationError: {
          ...state.conversationError,
          [(action.payload as string)]: null,
        },
      };

    case 'CLEAR_ACTIVE_DM_CONVERSATION':
      return {
        ...state,
        activeConversationId: null,
      };

    case 'SET_DM_MESSAGES_LOADING':
      return {
        ...state,
        conversationLoading: {
          ...state.conversationLoading,
          [(action.payload as string)]: true,
        },
      };

    case 'SET_DM_MESSAGES':
      return {
        ...state,
        messages: {
          ...state.messages,
          [(action.payload as { conversationId: string }).conversationId]: (action.payload as { messages?: ConversationMessage[] }).messages as ConversationMessage[],
        },
        paginationCursors: {
          ...state.paginationCursors,
          [(action.payload as { conversationId: string }).conversationId]: (action.payload as { nextCursor?: string | null }).nextCursor as string | null,
        },
        conversationLoading: {
          ...state.conversationLoading,
          [(action.payload as { conversationId: string }).conversationId]: false,
        },
        conversationError: {
          ...state.conversationError,
          [(action.payload as { conversationId: string }).conversationId]: null,
        },
      };

    case 'PREPEND_DM_MESSAGES':
      return {
        ...state,
        messages: {
          ...state.messages,
          [(action.payload as { conversationId: string }).conversationId]: [
            ...((action.payload as { messages?: ConversationMessage[] }).messages as ConversationMessage[]),
            ...(state.messages[(action.payload as { conversationId: string }).conversationId] || []),
          ],
        },
        paginationCursors: {
          ...state.paginationCursors,
          [(action.payload as { conversationId: string }).conversationId]: (action.payload as { nextCursor?: string | null }).nextCursor as string | null,
        },
        conversationLoading: {
          ...state.conversationLoading,
          [(action.payload as { conversationId: string }).conversationId]: false,
        },
      };

    case 'SET_DM_MESSAGES_ERROR':
      return {
        ...state,
        conversationError: {
          ...state.conversationError,
          [(action.payload as { conversationId: string }).conversationId]: (action.payload as { error?: string }).error as string,
        },
        conversationLoading: {
          ...state.conversationLoading,
          [(action.payload as { conversationId: string }).conversationId]: false,
        },
      };

    case 'APPEND_DM_MESSAGE': {
      const { conversationId, message } = action.payload as {
        conversationId: string;
        message: ConversationMessage;
      };
      const existing = state.messages[conversationId] || [];

      if (existing.some((msg: ConversationMessage) => msg._id === message._id)) {
        return state;
      }

      return {
        ...state,
        messages: {
          ...state.messages,
          [conversationId]: [...existing, message],
        },
      };
    }

    case 'REPLACE_OPTIMISTIC_MESSAGE':
      return {
        ...state,
        messages: {
          ...state.messages,
          [(action.payload as { conversationId: string }).conversationId]: (
            state.messages[(action.payload as { conversationId: string }).conversationId] || []
          ).map((msg: ConversationMessage) =>
            msg._id === (action.payload as { optimisticId?: string }).optimisticId
              ? ((action.payload as { serverMessage?: ConversationMessage }).serverMessage as ConversationMessage)
              : msg
          ),
        },
      };

    case 'REMOVE_OPTIMISTIC_MESSAGE':
      return {
        ...state,
        messages: {
          ...state.messages,
          [(action.payload as { conversationId: string }).conversationId]: (
            state.messages[(action.payload as { conversationId: string }).conversationId] || []
          ).filter((msg: ConversationMessage) => msg._id !== (action.payload as { optimisticId?: string }).optimisticId),
        },
      };

    case 'UPDATE_CONVERSATION_PREVIEW':
      return {
        ...state,
        conversations: state.conversations.map((conv: Conversation) =>
          conv._id === (action.payload as { conversationId: string }).conversationId
            ? {
                ...conv,
                lastMessagePreview: (action.payload as { lastMessagePreview?: any }).lastMessagePreview,
                lastMessageAt: (action.payload as { lastMessageAt?: string }).lastMessageAt,
                unreadCount:
                  conv._id === state.activeConversationId
                    ? 0
                    : ((action.payload as { isOwnMessage?: boolean }).isOwnMessage ? conv.unreadCount : conv.unreadCount + 1),
              }
            : conv
        ),
      };

    case 'CLEAR_UNREAD_COUNT':
      return {
        ...state,
        conversations: state.conversations.map((conv: Conversation) =>
          conv._id === (action.payload as string)
            ? { ...conv, unreadCount: 0 }
            : conv
        ),
      };

    case 'CREATE_DM_CONVERSATION':
      return {
        ...state,
        conversations: [(action.payload as Conversation), ...state.conversations],
      };

    default:
      return state;
  }
};

export const fetchDMConversations = () => {
  return async (dispatch: AppDispatch) => {
    dispatch({ type: 'SET_DM_LIST_LOADING' });
    try {
      const response = await directMessagesService.getConversations(20, 0);
      const conversations = response.data || response;
      dispatch({
        type: 'SET_DM_CONVERSATIONS',
        payload: conversations,
      });
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      dispatch({
        type: 'SET_DM_LIST_ERROR',
        payload: errorMessage || 'Failed to fetch conversations',
      });
    }
  };
};

export const fetchDMMessages = (conversationId: string, cursor: string | null = null) => {
  return async (dispatch: AppDispatch) => {
    dispatch({ type: 'SET_DM_MESSAGES_LOADING', payload: conversationId });
    try {
      const response = await directMessagesService.getConversationMessages(
        conversationId,
        20,
        cursor
      );

      const actionType = cursor ? 'PREPEND_DM_MESSAGES' : 'SET_DM_MESSAGES';
      dispatch({
        type: actionType,
        payload: {
          conversationId,
          messages: response.data || response.data,
          nextCursor: response.nextCursor || null,
        },
      });
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      dispatch({
        type: 'SET_DM_MESSAGES_ERROR',
        payload: {
          conversationId,
          error: errorMessage || 'Failed to fetch messages',
        },
      });
    }
  };
};

export const startDMConversation = (userId: string) => {
  return async (dispatch: AppDispatch) => {
    try {
      const response = await directMessagesService.createOrGetDM(userId);
      const conversation = response.data || response;

      if (!conversation.createdAt || Date.now() - new Date(conversation.createdAt).getTime() < 1000) {
        posthog.capture('dm_conversation_created', {
          conversation_id: conversation._id,
          other_user_id: userId,
        });
      }

      dispatch({
        type: 'CREATE_DM_CONVERSATION',
        payload: conversation,
      });

      dispatch({
        type: 'SET_ACTIVE_DM_CONVERSATION',
        payload: conversation._id,
      });

      return conversation;
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      dispatch({
        type: 'SET_DM_LIST_ERROR',
        payload: errorMessage || 'Failed to create conversation',
      });
      throw error;
    }
  };
};

export const sendDMMessage = (conversationId: string, text: string, userId: string, username: string) => {
  return async (dispatch: AppDispatch) => {
    const optimisticId = `optimistic_${Date.now()}_${Math.random()}`;

    const optimisticMessage: ConversationMessage = {
      _id: optimisticId,
      conversationId,
      senderId: userId,
      text,
      createdAt: new Date().toISOString(),
      isOptimistic: true,
    };

    dispatch({
      type: 'APPEND_DM_MESSAGE',
      payload: {
        conversationId,
        message: optimisticMessage,
      },
    });

    try {
      const response = await directMessagesService.sendMessage(
        conversationId,
        text,
        []
      );

      const serverMessage = response.data || response;

      dispatch({
        type: 'REPLACE_OPTIMISTIC_MESSAGE',
        payload: {
          conversationId,
          optimisticId,
          serverMessage,
        },
      });

      dispatch({
        type: 'UPDATE_CONVERSATION_PREVIEW',
        payload: {
          conversationId,
          lastMessagePreview: {
            text: serverMessage.text,
            senderId: serverMessage.senderId,
            createdAt: serverMessage.createdAt,
          },
          lastMessageAt: serverMessage.createdAt,
          isOwnMessage: true,
        },
      });

      return serverMessage;
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      dispatch({
        type: 'REMOVE_OPTIMISTIC_MESSAGE',
        payload: {
          conversationId,
          optimisticId,
        },
      });

      dispatch({
        type: 'SET_DM_MESSAGES_ERROR',
        payload: {
          conversationId,
          error: errorMessage || 'Failed to send message',
        },
      });

      throw error;
    }
  };
};

export const markDMAsRead = (conversationId: string) => {
  return async (dispatch: AppDispatch) => {
    try {
      await directMessagesService.markConversationAsRead(conversationId);

      dispatch({
        type: 'CLEAR_UNREAD_COUNT',
        payload: conversationId,
      });
    } catch (error) {
      console.error('Failed to mark conversation as read:', error);
    }
  };
};

export default directMessagesReducer;